import React from "react"

import { ResponsivePie } from "@nivo/pie"
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer } from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { useDriverPreferencesData } from "@/hooks/useDriverPreferencesData"
import { DateFilter } from "@/types/filters"
import { COLORS } from "@/utils/chartColors"

interface PreferencesTabProps {
  dateFilter: DateFilter
  jobId?: string
}

const PreferencesTab: React.FC<PreferencesTabProps> = ({ dateFilter, jobId }) => {
  const { data, isLoading } = useDriverPreferencesData(dateFilter, jobId)
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Preferred Weekly Blocks</CardTitle>
            <CardDescription>Number of blocks candidates prefer per week</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Skeleton className="h-[200px] w-[200px] rounded-full" />
              </div>
            ) : (
              <div className="h-[300px] w-full">
                <ResponsivePie
                  data={(data?.shiftPreferencesData || []).map((entry) => ({
                    id: entry.name,
                    label: entry.name,
                    value: entry.value,
                  }))}
                  margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={4}
                  colors={({ id }) => {
                    const entryIndex = (data?.shiftPreferencesData || []).findIndex(
                      (entry) => entry.name === id,
                    )
                    return COLORS[entryIndex % COLORS.length]
                  }}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: "color" }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor="#ffffff"
                  valueFormat={(value) => {
                    const total = (data?.shiftPreferencesData || []).reduce(
                      (sum, entry) => sum + (entry?.value ?? 0),
                      0,
                    )
                    if (!total || total <= 0) return `0%`
                    const pct = (Number(value ?? 0) / total) * 100
                    return `${pct.toFixed(1)}%`
                  }}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: "#999",
                      itemDirection: "left-to-right",
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: "circle",
                    },
                  ]}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Preferred Days of Week</CardTitle>
            <CardDescription>Days of the week candidates prefer to work</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ChartContainer
              className="h-80"
              config={{
                mon: { color: COLORS[0] },
                tue: { color: COLORS[1] },
                wed: { color: COLORS[2] },
                thu: { color: COLORS[3] },
                fri: { color: COLORS[4] },
                sat: { color: COLORS[5] },
                sun: { color: COLORS[6] },
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Skeleton className="h-[200px] w-full" />
                </div>
              ) : (
                <BarChart
                  width={500}
                  height={300}
                  data={data?.weekdayPreferencesData || []}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis type="number" allowDecimals={false} domain={[0, "dataMax"]} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              )}
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Van Ownership</CardTitle>
            <CardDescription>
              Distribution of drivers who own vans versus those who don't
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Skeleton className="h-[200px] w-[200px] rounded-full" />
              </div>
            ) : (
              <div className="h-[300px] w-full">
                <ResponsivePie
                  data={(data?.vanOwnershipData || []).map((entry) => ({
                    id: entry.name,
                    label: entry.name,
                    value: entry.value,
                  }))}
                  margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={4}
                  colors={({ id }) => {
                    const entryIndex = (data?.vanOwnershipData || []).findIndex(
                      (entry) => entry.name === id,
                    )
                    return COLORS[entryIndex % COLORS.length]
                  }}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: "color" }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor="#ffffff"
                  valueFormat={(value) => {
                    const total = (data?.vanOwnershipData || []).reduce(
                      (sum, entry) => sum + (entry?.value ?? 0),
                      0,
                    )
                    if (!total || total <= 0) return `0%`
                    const pct = (Number(value ?? 0) / total) * 100
                    return `${pct.toFixed(1)}%`
                  }}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: "#999",
                      itemDirection: "left-to-right",
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: "circle",
                    },
                  ]}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Age Distribution</CardTitle>
            <CardDescription>Age distribution of driver applicants</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ChartContainer
              className="h-80"
              config={{
                "18-24": { color: COLORS[0] },
                "25-34": { color: COLORS[1] },
                "35-44": { color: COLORS[2] },
                "45-54": { color: COLORS[3] },
                "55-64": { color: COLORS[4] },
                "65+": { color: COLORS[5] },
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Skeleton className="h-[200px] w-full" />
                </div>
              ) : (
                <BarChart
                  width={500}
                  height={300}
                  data={data?.ageDistributionData || []}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis type="number" allowDecimals={false} domain={[0, "dataMax"]} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PreferencesTab
